import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  FileText, Mail, Send, Save, CheckCircle, XCircle, Lock,
  ExternalLink, Database, Settings, Pencil, Eye, Info, MessageSquare, RotateCcw, AlertTriangle
} from "lucide-react";

const FORM_ICONS = {
  contacto: Mail,
  candidaturas: FileText,
  citas: Settings,
  whatsapp: MessageSquare,
  promociones: Send,
};

export default function FormConfigPage() {
  const { axiosAuth, isSuperAdmin } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  // Carga la lista. Se dispara en mount y cada vez que reloadKey cambia
  // (tras guardar). Patrón directo sin useCallback → robusto frente a
  // StrictMode y a navegación SPA.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        // ORDEN IMPORTA: diagnostics PRIMERO (read-only, ve estado real),
        // luego GET /form-config (puede auto-saner duplicados internamente).
        // Así el super_admin SIEMPRE ve el warning si la BD venía duplicada.
        if (isSuperAdmin) {
          try {
            const d = await axiosAuth().get("/form-config/diagnostics");
            if (mounted) setDuplicates(d.data || null);
          } catch {
            if (mounted) setDuplicates(null);
          }
        }
        const r = await axiosAuth().get("/form-config");
        if (mounted) setForms(r.data.forms || []);
      } catch {
        if (mounted) toast.error("Error al cargar formularios");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [axiosAuth, reloadKey, isSuperAdmin]);

  const openEdit = (form) => {
    setEditForm(form);
    setEditData({
      dest_email: form.dest_email || "",
      subject_template: form.subject_template || "",
      send_confirmation: form.send_confirmation ?? true,
      confirmation_subject: form.confirmation_subject || "",
      active: form.active ?? true,
    });
  };

  const handleSave = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      // PUT con la actualización
      const res = await axiosAuth().put(`/form-config/${editForm.form_id}`, editData);
      const saved = res.data || {};

      // VERIFICACIÓN ESTRICTA: el backend debe devolver el valor que pedimos.
      // Si no coincide, el "guardado" no es real y mostramos error.
      const expected = (editData.dest_email || "").trim();
      const actual = (saved.dest_email || "").trim();
      if (expected !== actual) {
        toast.error(
          `El servidor no persistió el cambio. Esperado "${expected}" pero devolvió "${actual}". Reintenta o contacta al equipo técnico.`
        );
        // No cerramos el modal — el usuario puede ver el valor que sí quedó.
        // Refrescamos la lista con el valor real del servidor.
        setForms((prev) => prev.map((f) => (
          f.form_id === editForm.form_id
            ? { ...f, ...saved }
            : f
        )));
        return;
      }

      // Aplicamos al estado local los valores REALES devueltos por el servidor
      // (no los del formulario local) — fuente única de verdad: el backend.
      setForms((prev) => prev.map((f) => (
        f.form_id === editForm.form_id
          ? { ...f, ...saved }
          : f
      )));
      toast.success("Configuración guardada");
      setEditForm(null);
      // Dispara una recarga REAL del servidor para confirmar.
      setReloadKey((k) => k + 1);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al guardar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    setRestoring(true);
    try {
      const res = await axiosAuth().post("/form-config/restore-defaults");
      const archived = res.data?.duplicates_archived || 0;
      toast.success(
        `Valores recomendados restaurados (${res.data?.restored?.length || 0} formularios)` +
        (archived > 0 ? ` · ${archived} duplicado(s) archivado(s)` : "")
      );
      setRestoreOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al restaurar valores recomendados";
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  const handleCleanupDuplicates = async () => {
    setCleaning(true);
    try {
      const res = await axiosAuth().post("/form-config/cleanup-duplicates");
      const archived = res.data?.duplicates_archived || 0;
      if (archived > 0) {
        toast.success(`${archived} duplicado(s) archivado(s) correctamente`);
      } else {
        toast.info("No se encontraron duplicados");
      }
      setReloadKey((k) => k + 1);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al limpiar duplicados";
      toast.error(msg);
    } finally {
      setCleaning(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-400">Cargando formularios...</div>;

  const centralized = forms.filter(f => f.managed_by === "centralizado");
  const systemManaged = forms.filter(f => f.managed_by === "sistema_propio");

  return (
    <div className="space-y-6" data-testid="form-config-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="form-config-title">
            Formularios y Destinatarios
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configuracion centralizada de todos los formularios publicos del sistema.
            Gestiona destinatarios, asuntos y confirmaciones desde un unico lugar.
          </p>
        </div>
        {isSuperAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRestoreOpen(true)}
            className="rounded-sm text-[10px] uppercase tracking-wider h-9 px-3 border-amber-300 text-amber-700 hover:bg-amber-50 flex-shrink-0"
            data-testid="restore-defaults-btn"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Restaurar valores recomendados
          </Button>
        )}
      </div>

      {/* Warning banner: duplicados detectados en BD */}
      {isSuperAdmin && duplicates?.has_duplicates && (
        <Card className="border-red-300 bg-red-50 rounded-sm" data-testid="duplicates-warning">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-red-900 mb-1">
                  Documentos duplicados detectados en la base de datos
                </p>
                <p className="text-xs text-red-700 mb-2">
                  Hay {duplicates.duplicates.length} formulario(s) con más de un documento
                  en MongoDB. La pantalla muestra siempre el ganador (recomendado &gt; más reciente),
                  pero limpia los duplicados para evitar comportamiento confuso.
                </p>
                <ul className="text-[11px] text-red-700 mb-3 list-disc list-inside space-y-0.5">
                  {duplicates.duplicates.map((d) => (
                    <li key={d.form_id} data-testid={`dup-row-${d.form_id}`}>
                      <code className="font-mono">{d.form_id}</code> · {d.count} docs · emails:{" "}
                      <span className="font-mono">{(d.all_dest_emails || []).join(", ")}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  onClick={handleCleanupDuplicates}
                  disabled={cleaning}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-[10px] uppercase tracking-wider h-8 px-3"
                  data-testid="cleanup-duplicates-btn"
                >
                  <Database className="w-3 h-3 mr-1" />
                  {cleaning ? "Limpiando..." : "Archivar duplicados ahora"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {forms.map(f => {
          const Icon = FORM_ICONS[f.form_id] || FileText;
          return (
            <Card key={f.form_id} className={`border rounded-sm ${f.active ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
              <CardContent className="p-3 text-center">
                <Icon className="w-5 h-5 mx-auto mb-1.5 text-slate-500" />
                <p className="text-xs font-bold text-slate-900">{f.form_name}</p>
                <div className="flex items-center justify-center gap-1.5 mt-1.5">
                  {f.sends_email ? (
                    <Badge variant="outline" className="h-4 text-[8px] border-emerald-200 text-emerald-600 bg-emerald-50 rounded-sm">Email</Badge>
                  ) : (
                    <Badge variant="outline" className="h-4 text-[8px] border-slate-200 text-slate-400 rounded-sm">Sin email</Badge>
                  )}
                  {f.saves_to_db && (
                    <Badge variant="outline" className="h-4 text-[8px] border-blue-200 text-blue-600 bg-blue-50 rounded-sm">BD</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Centralized forms */}
      <div>
        <h2 className="text-sm uppercase tracking-wider font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Pencil className="w-4 h-4 text-red-600" />
          Formularios editables (configuracion centralizada)
        </h2>
        <div className="space-y-3">
          {centralized.map(form => {
            const Icon = FORM_ICONS[form.form_id] || FileText;
            return (
              <Card key={form.form_id} className="border-slate-200 rounded-sm" data-testid={`form-card-${form.form_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-bold text-slate-900">{form.form_name}</span>
                        {form.active ? (
                          <Badge variant="outline" className="h-4 text-[8px] border-emerald-200 text-emerald-600 bg-emerald-50 rounded-sm">
                            <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Activo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="h-4 text-[8px] border-slate-200 text-slate-400 rounded-sm">
                            <XCircle className="w-2.5 h-2.5 mr-0.5" /> Inactivo
                          </Badge>
                        )}
                        <Badge variant="outline" className="h-4 text-[8px] border-blue-200 text-blue-600 bg-blue-50 rounded-sm">
                          <Pencil className="w-2.5 h-2.5 mr-0.5" /> Editable
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">{form.description}</p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1.5 text-xs">
                        <div>
                          <span className="text-slate-400 font-semibold">Pagina:</span>{" "}
                          <code className="bg-slate-100 px-1.5 py-0.5 rounded-sm text-slate-700">{form.page_route}</code>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold">Email destino:</span>{" "}
                          <span className={form.dest_email ? "text-slate-700 font-medium" : "text-amber-500"}>
                            {form.dest_email || "Sin configurar"}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold">Confirmacion:</span>{" "}
                          <span className={form.send_confirmation ? "text-emerald-600" : "text-slate-400"}>
                            {form.send_confirmation ? "Si" : "No"}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold">Trazabilidad:</span>{" "}
                          <span className="text-emerald-600 flex items-center gap-1 inline-flex">
                            <Database className="w-3 h-3" /> {form.db_collection}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs mt-1.5">
                        <span className="text-slate-400 font-semibold">Asunto:</span>{" "}
                        <span className="text-slate-600 font-mono">{form.subject_template}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openEdit(form)}
                      className="rounded-sm text-[10px] uppercase tracking-wider h-8 px-3 flex-shrink-0"
                      data-testid={`edit-form-${form.form_id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Editar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* System-managed forms (read-only) */}
      <div>
        <h2 className="text-sm uppercase tracking-wider font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-400" />
          Formularios con sistema propio (solo lectura)
        </h2>
        <div className="space-y-3">
          {systemManaged.map(form => {
            const Icon = FORM_ICONS[form.form_id] || FileText;
            return (
              <Card key={form.form_id} className="border-slate-200 rounded-sm bg-slate-50/50" data-testid={`form-card-${form.form_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-bold text-slate-900">{form.form_name}</span>
                        {form.active ? (
                          <Badge variant="outline" className="h-4 text-[8px] border-emerald-200 text-emerald-600 bg-emerald-50 rounded-sm">
                            <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Activo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="h-4 text-[8px] border-slate-200 text-slate-400 rounded-sm">
                            <XCircle className="w-2.5 h-2.5 mr-0.5" /> Inactivo
                          </Badge>
                        )}
                        <Badge variant="outline" className="h-4 text-[8px] border-amber-200 text-amber-600 bg-amber-50 rounded-sm">
                          <Lock className="w-2.5 h-2.5 mr-0.5" /> Sistema propio
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">{form.description}</p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1.5 text-xs">
                        <div>
                          <span className="text-slate-400 font-semibold">Pagina:</span>{" "}
                          <code className="bg-white px-1.5 py-0.5 rounded-sm text-slate-700 border border-slate-200">{form.page_route}</code>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold">Email:</span>{" "}
                          <span className="text-slate-500 italic">{form.sends_email ? form.dest_email || "Via sistema propio" : "No aplica"}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold">Confirmacion:</span>{" "}
                          <span className={form.send_confirmation ? "text-emerald-600" : "text-slate-400"}>
                            {form.send_confirmation ? "Si (sistema propio)" : "No"}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold">Trazabilidad:</span>{" "}
                          <span className="text-emerald-600 flex items-center gap-1 inline-flex">
                            <Database className="w-3 h-3" /> {form.db_collection}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs mt-1.5 flex items-center gap-1.5">
                        <Info className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        <span className="text-blue-600">
                          Se configura desde: <strong>{form.config_location}</strong>
                        </span>
                      </div>
                      {form.admin_page && (
                        <div className="text-xs mt-1">
                          <span className="text-slate-400 font-semibold">Gestion:</span>{" "}
                          <a href={form.admin_page} className="text-blue-600 hover:underline inline-flex items-center gap-1">
                            {form.admin_page} <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" disabled
                      className="rounded-sm text-[10px] uppercase tracking-wider h-8 px-3 flex-shrink-0 opacity-40"
                      data-testid={`view-form-${form.form_id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Solo lectura
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Leyenda</p>
          <div className="flex flex-wrap gap-4 text-xs text-slate-600">
            <span className="flex items-center gap-1.5">
              <Badge variant="outline" className="h-4 text-[8px] border-emerald-200 text-emerald-600 bg-emerald-50 rounded-sm">Email</Badge>
              Envia email al destinatario
            </span>
            <span className="flex items-center gap-1.5">
              <Badge variant="outline" className="h-4 text-[8px] border-blue-200 text-blue-600 bg-blue-50 rounded-sm">BD</Badge>
              Guarda trazabilidad en base de datos
            </span>
            <span className="flex items-center gap-1.5">
              <Badge variant="outline" className="h-4 text-[8px] border-blue-200 text-blue-600 bg-blue-50 rounded-sm">Editable</Badge>
              Configurable desde esta pagina
            </span>
            <span className="flex items-center gap-1.5">
              <Badge variant="outline" className="h-4 text-[8px] border-amber-200 text-amber-600 bg-amber-50 rounded-sm">Sistema propio</Badge>
              Gestionado por su propio sistema
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editForm} onOpenChange={(open) => { if (!open) setEditForm(null); }}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="form-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Pencil className="w-4 h-4 text-red-600" />
              Editar: {editForm?.form_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email destinatario</Label>
              <p className="text-[10px] text-slate-400 mb-1">A quien llega el aviso cuando alguien envia este formulario.</p>
              <Input value={editData.dest_email} onChange={e => setEditData(p => ({...p, dest_email: e.target.value}))}
                placeholder="correo@empresa.com" className="rounded-sm h-9 border-slate-200" data-testid="form-dest-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Asunto del email de notificacion</Label>
              <p className="text-[10px] text-slate-400 mb-1">Variables disponibles: {"{name}"}, {"{reason}"}, {"{area}"}, {"{email}"}</p>
              <Input value={editData.subject_template} onChange={e => setEditData(p => ({...p, subject_template: e.target.value}))}
                placeholder="Nueva consulta: {reason} — {name}" className="rounded-sm h-9 border-slate-200 font-mono text-xs" data-testid="form-subject-input" />
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Confirmacion al usuario</Label>
                <p className="text-[10px] text-slate-400">Enviar email de confirmacion cuando el usuario envia el formulario.</p>
              </div>
              <Switch checked={editData.send_confirmation} onCheckedChange={v => setEditData(p => ({...p, send_confirmation: v}))}
                data-testid="form-confirmation-toggle" />
            </div>
            {editData.send_confirmation && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Asunto del email de confirmacion</Label>
                <Input value={editData.confirmation_subject} onChange={e => setEditData(p => ({...p, confirmation_subject: e.target.value}))}
                  placeholder="Hemos recibido tu consulta — ASCAUTO" className="rounded-sm h-9 border-slate-200 text-xs" data-testid="form-conf-subject-input" />
              </div>
            )}
            <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Formulario activo</Label>
                <p className="text-[10px] text-slate-400">Si esta desactivado, no se enviaran emails (los datos si se guardan).</p>
              </div>
              <Switch checked={editData.active} onCheckedChange={v => setEditData(p => ({...p, active: v}))}
                data-testid="form-active-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditForm(null)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="form-save-btn">
              <Save className="w-3 h-3 mr-1" /> {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Restore defaults confirmation dialog (super_admin only) */}
      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="restore-defaults-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <RotateCcw className="w-4 h-4 text-amber-600" />
              Restaurar valores recomendados
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 pt-2">
              Esta accion sobrescribira los emails destino y asuntos de los
              formularios <strong>Contacto</strong> y <strong>Trabaja con nosotros</strong> con los valores oficiales de ASCAUTO.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs space-y-2 py-2">
            <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2">
              <span className="text-slate-500">Contacto:</span>
              <code className="font-mono text-emerald-700">ascauto@ascauto.com</code>
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2">
              <span className="text-slate-500">Trabaja con nosotros:</span>
              <code className="font-mono text-emerald-700">ascauto@ascauto.com</code>
            </div>
            <p className="text-[10px] text-slate-400 pt-1">
              No afecta a citas, WhatsApp ni promociones (gestionados por sistema propio).
              No borra solicitudes ni trazabilidad en base de datos.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)} className="rounded-sm" disabled={restoring}>
              Cancelar
            </Button>
            <Button
              onClick={handleRestoreDefaults}
              disabled={restoring}
              className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm"
              data-testid="restore-defaults-confirm-btn"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> {restoring ? "Restaurando..." : "Restaurar ahora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
